import { useState, useCallback, useMemo } from 'react';
import type { FacetKey } from '../components/search/SearchFilterRail';

export type { FacetKey };

/**
 * The five standard catalog facets, in Getty wizard order (GH#1264).
 * Module-level constant so pages can pass it straight to
 * `useBrowse({ facets: ALL_CATALOG_FACETS })` — useBrowse depends on the
 * array's IDENTITY, so an inline literal would refetch on every render.
 */
export const ALL_CATALOG_FACETS: readonly FacetKey[] = [
  'workType',
  'materials',
  'techniques',
  'creators',
  'decade',
];

/**
 * Selections serialized into the browseApi/useBrowse param shape — array
 * values (never undefined) keyed by the same facet names browseApi and
 * SearchContext use on `/api/search`. An empty array means "no filter on
 * this facet", matching browseApi's `if (params.x?.length)` guard.
 */
export type CatalogFacetBrowseParams = Record<FacetKey, string[]>;

export interface UseCatalogFacetFiltersReturn {
  selected: Record<FacetKey, Set<string>>;
  toggle: (key: FacetKey, value: string) => void;
  clearAll: () => void;
  filtersActive: boolean;
  browseParams: CatalogFacetBrowseParams;
}

/**
 * Fresh empty-selection state. A factory (not a module singleton) so no Set
 * instance is ever shared across hook instances or resets — a singleton's
 * Sets would be one accidental in-place mutation away from cross-page
 * state bleed.
 */
const emptySelected = (): Record<FacetKey, Set<string>> => ({
  workType: new Set(),
  materials: new Set(),
  techniques: new Set(),
  creators: new Set(),
  decade: new Set(),
});

/**
 * Owns facet selection state for the catalog filter rail (GH#1263/GH#1265),
 * shared by PublicCatalog and My Objects. Selections clear whenever
 * `scopeKey` changes — the caller passes an identity string for whatever
 * "catalog" means on that page (e.g. `aliId` on PublicCatalog;
 * `${activeCatalogAliId}:${activeCatalogOwnerId}:${albumId}` on My Objects)
 * so switching catalogs/owners/albums doesn't carry stale filters over.
 */
export const useCatalogFacetFilters = (scopeKey: string): UseCatalogFacetFiltersReturn => {
  const [selected, setSelected] = useState<Record<FacetKey, Set<string>>>(emptySelected);
  // Adjust state during render (React's documented pattern for "reset state
  // when a prop changes") rather than in an effect — an effect would commit
  // the stale render first and then cascade a second render to clear it.
  const [prevScopeKey, setPrevScopeKey] = useState(scopeKey);
  if (scopeKey !== prevScopeKey) {
    setPrevScopeKey(scopeKey);
    setSelected(emptySelected());
  }

  const toggle = useCallback((key: FacetKey, value: string) => {
    setSelected((prev) => {
      const nextValues = new Set(prev[key]);
      if (nextValues.has(value)) {
        nextValues.delete(value);
      } else {
        nextValues.add(value);
      }
      return { ...prev, [key]: nextValues };
    });
  }, []);

  const clearAll = useCallback(() => {
    setSelected(emptySelected());
  }, []);

  const filtersActive = useMemo(
    () => (Object.keys(selected) as FacetKey[]).some((key) => selected[key].size > 0),
    [selected],
  );

  const browseParams = useMemo<CatalogFacetBrowseParams>(
    () => ({
      workType: Array.from(selected.workType),
      materials: Array.from(selected.materials),
      techniques: Array.from(selected.techniques),
      creators: Array.from(selected.creators),
      decade: Array.from(selected.decade),
    }),
    [selected],
  );

  return { selected, toggle, clearAll, filtersActive, browseParams };
};
